import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Save, Upload, RotateCcw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/branding")({
  head: () => ({
    meta: [
      { title: "Admin — Huisstijl beheren" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminBrandingPage,
});

type ThemeKey = "night_deep" | "night" | "gold" | "amber_glow" | "cream" | "foreground" | "card";

type BrandingRow = {
  logo_url: string | null;
  logo_full_url: string | null;
  favicon_url: string | null;
  theme: Partial<Record<ThemeKey, string>>;
};

const THEME_FIELDS: { key: ThemeKey; label: string; hint: string; fallback: string }[] = [
  { key: "night_deep", label: "Achtergrond (Night Deep)", hint: "Hoofdachtergrond van de site", fallback: "#0a1224" },
  { key: "night", label: "Nacht", hint: "Secundaire donkere tint", fallback: "#12203a" },
  { key: "gold", label: "Goud (primair accent)", hint: "Knoppen, links, accenten", fallback: "#d4a84c" },
  { key: "amber_glow", label: "Amber glow", hint: "Warm licht / secundair accent", fallback: "#e6c26a" },
  { key: "cream", label: "Crème", hint: "Lichte achtergronden (partner kaarten)", fallback: "#ede3d0" },
  { key: "foreground", label: "Tekstkleur", hint: "Standaard tekstkleur", fallback: "#f0eadf" },
  { key: "card", label: "Kaart", hint: "Kaart-achtergronden", fallback: "#1b2a4a" },
];

function useAdmin() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return setIsAdmin(false);
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", u.user.id)
        .eq("role", "admin");
      setIsAdmin(!!roles && roles.length > 0);
    })();
  }, []);
  return isAdmin;
}

function AdminBrandingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = useAdmin();
  const [row, setRow] = useState<BrandingRow | null>(null);
  const [previews, setPreviews] = useState<{ logo: string | null; logoFull: string | null; favicon: string | null }>({
    logo: null,
    logoFull: null,
    favicon: null,
  });
  const [saving, setSaving] = useState(false);
  const [starfieldEnabled, setStarfieldEnabled] = useState<boolean | null>(null);
  const [savingStarfield, setSavingStarfield] = useState(false);
  const [gaId, setGaId] = useState<string>("");
  const [gaLoaded, setGaLoaded] = useState(false);
  const [savingGa, setSavingGa] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "starfield_enabled")
        .maybeSingle();
      setStarfieldEnabled(data?.value === false ? false : true);
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "google_analytics_id")
        .maybeSingle();
      setGaId(typeof data?.value === "string" ? data.value : "");
      setGaLoaded(true);
    })();
  }, [isAdmin]);

  async function saveGaId() {
    const trimmed = gaId.trim();
    if (trimmed && !/^(G-[A-Z0-9]+|UA-\d+-\d+|AW-\d+|GTM-[A-Z0-9]+)$/i.test(trimmed)) {
      return toast.error("Ongeldige ID. Formaat: G-XXXXXXX (of GTM-/AW-/UA-).");
    }
    setSavingGa(true);
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "google_analytics_id", value: trimmed }, { onConflict: "key" });
    setSavingGa(false);
    if (error) return toast.error(error.message);
    setGaId(trimmed);
    toast.success(trimmed ? "Google-tag opgeslagen" : "Google-tag uitgeschakeld");
    qc.invalidateQueries({ queryKey: ["site_settings", "google_analytics_id"] });
  }

  async function toggleStarfield(next: boolean) {
    setSavingStarfield(true);
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "starfield_enabled", value: next }, { onConflict: "key" });
    setSavingStarfield(false);
    if (error) return toast.error(error.message);
    setStarfieldEnabled(next);
    toast.success(next ? "Sterrenhemel ingeschakeld" : "Sterrenhemel uitgeschakeld");
  }

  async function load() {
    const { data, error } = await supabase
      .from("site_branding")
      .select("logo_url, logo_full_url, favicon_url, theme")
      .eq("id", "default")
      .maybeSingle();
    if (error) return toast.error(error.message);
    const record: BrandingRow = {
      logo_url: data?.logo_url ?? null,
      logo_full_url: data?.logo_full_url ?? null,
      favicon_url: data?.favicon_url ?? null,
      theme: (data?.theme as BrandingRow["theme"]) ?? {},
    };
    setRow(record);
    const sign = async (p: string | null) => {
      if (!p) return null;
      const { data } = await supabase.storage.from("branding").createSignedUrl(p, 60 * 60);
      return data?.signedUrl ?? null;
    };
    const [logo, logoFull, favicon] = await Promise.all([
      sign(record.logo_url),
      sign(record.logo_full_url),
      sign(record.favicon_url),
    ]);
    setPreviews({ logo, logoFull, favicon });
  }

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  async function uploadFile(kind: "logo" | "logoFull" | "favicon", file: File) {
    const ext = file.name.split(".").pop() || "png";
    const path = `${kind}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const up = await supabase.storage.from("branding").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    if (up.error) {
      toast.error("Upload mislukt: " + up.error.message);
      return;
    }
    const patch =
      kind === "logo"
        ? { logo_url: path }
        : kind === "logoFull"
          ? { logo_full_url: path }
          : { favicon_url: path };
    const { error } = await supabase.from("site_branding").update(patch).eq("id", "default");
    if (error) return toast.error(error.message);
    toast.success("Geüpload");
    await load();
    qc.invalidateQueries({ queryKey: ["branding"] });
  }

  async function removeFile(kind: "logo" | "logoFull" | "favicon") {
    const patch =
      kind === "logo"
        ? { logo_url: null }
        : kind === "logoFull"
          ? { logo_full_url: null }
          : { favicon_url: null };
    const { error } = await supabase.from("site_branding").update(patch).eq("id", "default");
    if (error) return toast.error(error.message);
    toast.success("Verwijderd — standaard hersteld");
    await load();
    qc.invalidateQueries({ queryKey: ["branding"] });
  }

  function setThemeField(key: ThemeKey, value: string) {
    setRow((prev) => (prev ? { ...prev, theme: { ...prev.theme, [key]: value } } : prev));
  }

  async function saveTheme() {
    if (!row) return;
    setSaving(true);
    // Strip empty strings so tokens fall back to defaults.
    const cleaned: Record<string, string> = {};
    for (const [k, v] of Object.entries(row.theme)) {
      if (v && typeof v === "string" && v.trim()) cleaned[k] = v.trim();
    }
    const { error } = await supabase
      .from("site_branding")
      .update({ theme: cleaned })
      .eq("id", "default");
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Kleuren opgeslagen");
    qc.invalidateQueries({ queryKey: ["branding"] });
  }

  async function resetTheme() {
    if (!confirm("Alle kleuren terugzetten naar standaard?")) return;
    const { error } = await supabase
      .from("site_branding")
      .update({ theme: {} })
      .eq("id", "default");
    if (error) return toast.error(error.message);
    // Also strip any live overrides immediately.
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      ["--night-deep","--night","--gold","--amber-glow","--cream","--foreground","--card"].forEach((v) =>
        root.style.removeProperty(v),
      );
    }
    toast.success("Standaard hersteld");
    await load();
    qc.invalidateQueries({ queryKey: ["branding"] });
  }

  if (isAdmin === null || row === null) {
    return <div className="flex min-h-screen items-center justify-center text-foreground/70">Laden…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="font-display text-2xl">Geen toegang</h1>
          <p className="text-foreground/70">Je account heeft geen admin rechten.</p>
          <Button onClick={handleSignOut} variant="ghostLine">Uitloggen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-5 py-12 md:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <p className="font-mono-cinema text-[10px] text-gold">— Admin</p>
          <h1 className="mt-2 font-display text-3xl font-medium md:text-4xl">Huisstijl beheren</h1>
          <p className="mt-2 text-sm text-foreground/70">Logo, favicon en kleuren van de website.</p>
        </div>

        {/* LOGOS */}
        <section className="rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8">
          <h2 className="font-display text-xl">Logo &amp; favicon</h2>
          <p className="mt-1 text-sm text-foreground/70">
            Upload transparante PNG's of SVG's. Leeg laten → standaard logo wordt gebruikt.
          </p>

          <div className="mt-6 grid gap-6 md:grid-cols-3">
            <BrandingSlot
              label="Volledig logo (nav + footer)"
              preview={previews.logoFull}
              bg="dark"
              onFile={(f) => uploadFile("logoFull", f)}
              onRemove={() => removeFile("logoFull")}
              currentPath={row.logo_full_url}
            />
            <BrandingSlot
              label="Compact logo (optioneel)"
              preview={previews.logo}
              bg="dark"
              onFile={(f) => uploadFile("logo", f)}
              onRemove={() => removeFile("logo")}
              currentPath={row.logo_url}
            />
            <BrandingSlot
              label="Favicon (PNG of SVG, vierkant)"
              preview={previews.favicon}
              bg="light"
              onFile={(f) => uploadFile("favicon", f)}
              onRemove={() => removeFile("favicon")}
              currentPath={row.favicon_url}
            />
          </div>
        </section>

        {/* THEME */}
        <section className="mt-8 rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8">
          <div className="mb-6 rounded-xl border border-border/70 bg-night/40 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg">Google-tag (Analytics)</h3>
                <p className="mt-1 text-xs text-foreground/60">
                  Meet-ID (bv. <code className="font-mono-cinema">G-XXXXXXX</code>). Wordt pas geladen
                  na cookie-toestemming. Leeg = geen tracking.
                </p>
              </div>
              <Button variant="hero" size="sm" onClick={saveGaId} disabled={!gaLoaded || savingGa}>
                <Save className="h-4 w-4" /> {savingGa ? "Bezig…" : "Opslaan"}
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Input
                value={gaId}
                placeholder="G-XXXXXXX"
                onChange={(e) => setGaId(e.target.value)}
                className="font-mono-cinema text-xs max-w-xs"
                disabled={!gaLoaded}
              />
              {gaId && (
                <Button variant="ghostLine" size="sm" onClick={() => setGaId("")}>
                  Wissen
                </Button>
              )}
            </div>
          </div>

          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/70 bg-night/40 p-4">
            <div>
              <h3 className="font-display text-lg">Sterrenhemel-animatie</h3>
              <p className="mt-1 text-xs text-foreground/60">
                Rustige animatie op de achtergrond van alle pagina's.
              </p>
            </div>
            <Button
              variant={starfieldEnabled ? "ghostLine" : "hero"}
              size="sm"
              disabled={starfieldEnabled === null || savingStarfield}
              onClick={() => toggleStarfield(!starfieldEnabled)}
            >
              {savingStarfield
                ? "Bezig…"
                : starfieldEnabled
                  ? "Uitschakelen"
                  : "Inschakelen"}
            </Button>
          </div>

          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-xl">Kleuren</h2>
              <p className="mt-1 text-sm text-foreground/70">
                Kies hex-kleuren (bv. <code className="font-mono-cinema text-[10px]">#d4a84c</code>).
                Leeg = standaard.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghostLine" size="sm" onClick={resetTheme}>
                <RotateCcw className="h-4 w-4" /> Terug naar standaard
              </Button>
              <Button variant="hero" size="sm" onClick={saveTheme} disabled={saving}>
                <Save className="h-4 w-4" /> {saving ? "Bezig…" : "Opslaan"}
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {THEME_FIELDS.map((f) => {
              const val = row.theme[f.key] ?? "";
              const displayHex = val && /^#[0-9a-fA-F]{6}$/.test(val) ? val : f.fallback;
              return (
                <div key={f.key} className="rounded-xl border border-border/70 bg-night/40 p-4">
                  <Label className="text-sm font-medium">{f.label}</Label>
                  <p className="mt-0.5 text-xs text-foreground/60">{f.hint}</p>
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      type="color"
                      aria-label={f.label}
                      value={displayHex}
                      onChange={(e) => setThemeField(f.key, e.target.value)}
                      className="h-11 w-14 cursor-pointer rounded-md border border-border bg-transparent p-1"
                    />
                    <Input
                      value={val}
                      placeholder={f.fallback}
                      onChange={(e) => setThemeField(f.key, e.target.value)}
                      className="font-mono-cinema text-xs"
                    />
                    {val && (
                      <Button
                        variant="ghostLine"
                        size="sm"
                        onClick={() => setThemeField(f.key, "")}
                        title="Standaard"
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-xs text-foreground/60">
            Tip: klik op “Opslaan” en herlaad de pagina om het resultaat op de hele site te zien.
          </p>
        </section>
      </div>
      <Toaster position="top-center" />
    </div>
  );
}

function BrandingSlot({
  label,
  preview,
  bg,
  onFile,
  onRemove,
  currentPath,
}: {
  label: string;
  preview: string | null;
  bg: "light" | "dark";
  onFile: (f: File) => void;
  onRemove: () => void;
  currentPath: string | null;
}) {
  const bgClass = bg === "light" ? "bg-cream" : "bg-night-deep";
  return (
    <div className="rounded-xl border border-border/70 bg-night/40 p-4">
      <Label className="text-sm font-medium">{label}</Label>
      <div className={`mt-3 flex h-28 items-center justify-center rounded-lg ${bgClass}`}>
        {preview ? (
          <img src={preview} alt={label} className="max-h-20 max-w-[80%] object-contain" />
        ) : (
          <span className="text-xs text-foreground/50">Standaard actief</span>
        )}
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-secondary">
          <Upload className="h-4 w-4" />
          <span>{currentPath ? "Vervangen" : "Uploaden"}</span>
          <input
            type="file"
            accept="image/png,image/svg+xml,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.currentTarget.value = "";
            }}
          />
        </label>
        {currentPath && (
          <Button variant="ghostLine" size="sm" onClick={onRemove}>
            Terug naar standaard
          </Button>
        )}
      </div>
    </div>
  );
}